/**
 * ExternalViewLayout — polished portal shell for planTEK external views.
 *
 * Responsive header: logo (left), view title (center), view switcher + user menu (right).
 * Panel grid: single column mobile → two column tablet+ → custom on desktop.
 * Theme: CSS variables only, zero hardcoded colors.
 * Animations: subtle fade-in + slide-up per panel.
 *
 * Preview admin banner: when ?preview=true is in the URL query string,
 * an informational banner is shown at the top for internal admins only.
 */
import { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown, LayoutGrid, Eye, X as XIcon } from 'lucide-react';
import { PoweredByFooter } from '@/components/external/PoweredByFooter';
import { UserMenuButton } from '@/components/external/UserMenuButton';
import type { ExternalUser } from '@/components/external/UserMenuButton';

export interface ExternalView {
  id: string;
  name: string;
  panels: ExternalPanel[];
  branding?: {
    logo_url?: string | null;
    company_name?: string | null;
  };
  layout?: {
    columns?: 1 | 2 | 'auto';
  };
  welcome_message?: string | null;
}

export interface ExternalPanel {
  type: string;
  title: string;
  data?: any;
}

interface ExternalViewLayoutProps {
  /** The currently displayed view */
  view: ExternalView;
  /** All views assigned to this user (for view switcher) */
  views?: ExternalView[];
  /** Called when user selects a different view */
  onViewSwitch?: (viewId: string) => void;
  /** Authenticated external user info for avatar/menu */
  user?: ExternalUser;
  /** Called when user clicks sign out */
  onLogout?: () => void;
  /** Panel content — rendered into the grid */
  children: React.ReactNode;
}

export function ExternalViewLayout({
  view,
  views = [],
  onViewSwitch,
  user,
  onLogout,
  children,
}: ExternalViewLayoutProps) {
  const [viewSwitcherOpen, setViewSwitcherOpen] = useState(false);
  const switcherRef = useRef<HTMLDivElement>(null);
  const hasMultipleViews = views.length > 1;

  // Preview banner: shown when ?preview=true is in the URL (admin-only indicator)
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') === 'true';
  const previewAs = searchParams.get('as') ?? null;
  const [previewBannerDismissed, setPreviewBannerDismissed] = useState(false);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (switcherRef.current && !switcherRef.current.contains(e.target as Node)) {
        setViewSwitcherOpen(false);
      }
    }
    if (viewSwitcherOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [viewSwitcherOpen]);

  // Close on Escape
  useEffect(() => {
    function handleEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setViewSwitcherOpen(false);
    }
    if (viewSwitcherOpen) document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [viewSwitcherOpen]);

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--background)' }}>
      {/* Admin preview banner — only shown when ?preview=true */}
      {isPreview && !previewBannerDismissed && (
        <div
          className="flex items-center justify-between px-4 py-2 text-sm"
          style={{
            backgroundColor: 'color-mix(in srgb, var(--primary) 15%, transparent)',
            borderBottom: '1px solid color-mix(in srgb, var(--primary) 30%, transparent)',
            color: 'var(--foreground)',
          }}
          data-testid="preview-admin-banner"
        >
          <div className="flex items-center gap-2">
            <Eye className="h-4 w-4 shrink-0" style={{ color: 'var(--primary)' }} />
            <span>
              {previewAs
                ? `You are previewing this portal as ${previewAs}`
                : 'You are previewing this portal as an admin'
              }. External users won&apos;t see this banner.
            </span>
          </div>
          <button
            onClick={() => setPreviewBannerDismissed(true)}
            className="ml-4 shrink-0 rounded p-0.5 hover:opacity-70 transition-opacity"
            aria-label="Dismiss preview banner"
            data-testid="preview-banner-dismiss"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Sticky header */}
      <header
        className="sticky top-0 z-50 border-b"
        style={{
          backgroundColor: 'var(--background)',
          borderColor: 'var(--border, #e5e7eb)',
        }}
      >
        <div className="mx-auto max-w-6xl px-4 sm:px-6 min-h-16 py-2 sm:py-0 sm:h-16 flex items-center gap-3">
          {/* Left: logo — tighter on mobile so the title can breathe (P9) */}
          <div className="flex items-center gap-2.5 shrink-0 w-16 sm:w-48">
            {view.branding?.logo_url ? (
              <img
                src={view.branding.logo_url}
                alt={view.branding.company_name || 'Logo'}
                className="h-8 max-w-[60px] sm:max-w-[120px] object-contain"
              />
            ) : view.branding?.company_name ? (
              <span
                className="text-base font-bold tracking-tight truncate"
                style={{ color: 'var(--foreground)' }}
              >
                {view.branding.company_name}
              </span>
            ) : (
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: 'var(--primary)' }}
              >
                <LayoutGrid className="w-4 h-4" style={{ color: 'var(--primary-foreground, #fff)' }} />
              </div>
            )}
          </div>

          {/* Center: view title — wraps to two lines on mobile instead of
              ellipsing to "Ba..." (P9). */}
          <div className="flex-1 flex justify-center px-1 sm:px-4 min-w-0">
            <h1
              className="text-xs sm:text-sm font-semibold text-center leading-tight break-words"
              style={{
                color: 'var(--foreground)',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {view.name}
            </h1>
          </div>

          {/* Right: view switcher + user menu — tighter on mobile (P9) */}
          <div className="flex items-center gap-2 shrink-0 w-16 sm:w-48 justify-end">
            {hasMultipleViews && (
              <div className="relative" ref={switcherRef}>
                <button
                  onClick={() => setViewSwitcherOpen((v) => !v)}
                  className="flex items-center gap-1.5 px-3 py-1.5 min-h-[44px] sm:min-h-0 rounded-lg text-xs font-medium transition-colors"
                  style={{
                    color: 'var(--muted-foreground, #6b7280)',
                    backgroundColor: viewSwitcherOpen
                      ? 'color-mix(in srgb, var(--primary) 10%, transparent)'
                      : 'transparent',
                  }}
                  aria-haspopup="listbox"
                  aria-expanded={viewSwitcherOpen}
                >
                  <span className="hidden sm:block truncate max-w-[100px]">Switch view</span>
                  <ChevronDown
                    className="w-3.5 h-3.5 transition-transform"
                    style={{ transform: viewSwitcherOpen ? 'rotate(180deg)' : 'none' }}
                  />
                </button>

                {viewSwitcherOpen && (
                  <div
                    className="absolute right-0 top-full mt-1 min-w-[180px] rounded-xl shadow-lg border overflow-hidden z-50"
                    style={{
                      backgroundColor: 'var(--background)',
                      borderColor: 'var(--border, #e5e7eb)',
                    }}
                    role="listbox"
                  >
                    <div
                      className="px-3 py-2 border-b"
                      style={{ borderColor: 'var(--border, #e5e7eb)' }}
                    >
                      <p
                        className="text-xs font-semibold uppercase tracking-wide"
                        style={{ color: 'var(--muted-foreground, #6b7280)' }}
                      >
                        Your Views
                      </p>
                    </div>
                    {views.map((v) => (
                      <button
                        key={v.id}
                        role="option"
                        aria-selected={v.id === view.id}
                        onClick={() => {
                          setViewSwitcherOpen(false);
                          onViewSwitch?.(v.id);
                        }}
                        className="w-full text-left px-3 py-3 min-h-[44px] text-sm transition-colors"
                        style={{
                          backgroundColor:
                            v.id === view.id
                              ? 'color-mix(in srgb, var(--primary) 10%, transparent)'
                              : 'transparent',
                          color:
                            v.id === view.id
                              ? 'var(--primary)'
                              : 'var(--foreground)',
                          fontWeight: v.id === view.id ? 600 : 400,
                        }}
                      >
                        {v.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <UserMenuButton user={user} onLogout={onLogout} />
          </div>
        </div>
      </header>

      {/* Welcome message banner */}
      {view.welcome_message && (
        <div
          className="border-b px-4 sm:px-6 py-3"
          style={{
            backgroundColor: 'color-mix(in srgb, var(--primary) 8%, var(--background))',
            borderColor: 'var(--border, #e5e7eb)',
          }}
        >
          <p
            className="mx-auto max-w-6xl text-sm"
            style={{ color: 'var(--foreground)' }}
          >
            {view.welcome_message}
          </p>
        </div>
      )}

      {/* Main content area */}
      <main className="flex-1 mx-auto w-full max-w-6xl px-3 sm:px-6 py-4 sm:py-8">
        {children}
      </main>

      <PoweredByFooter />
    </div>
  );
}

/**
 * PanelGrid — responsive two-column grid for panels.
 * Supports 1-col (mobile), 2-col (tablet+), or explicit column count.
 *
 * On mobile the grid is a flex-col so that CSS `order` on individual panels
 * can enforce priority ordering (project_summary first, invoices last).
 * On md+ it switches to a CSS grid so panels flow into two columns.
 */
interface PanelGridProps {
  columns?: 1 | 2 | 'auto';
  children: React.ReactNode;
}

export function PanelGrid({ columns = 'auto', children }: PanelGridProps) {
  // Mobile: flex column so `order` CSS works for priority stacking.
  // Tablet+: grid for two-column layout.
  const gridClass =
    columns === 1
      ? 'flex flex-col gap-4'
      : columns === 2
      ? 'flex flex-col gap-4 md:grid md:grid-cols-2'
      : 'flex flex-col gap-4 md:grid md:grid-cols-2';

  return <div className={gridClass}>{children}</div>;
}

/**
 * PanelCard — consistent card wrapper for all panel types.
 * Includes title bar, optional actions slot, and content area.
 */
interface PanelCardProps {
  title: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  /** Index used to stagger entrance animations */
  index?: number;
}

export function PanelCard({ title, actions, children, index = 0 }: PanelCardProps) {
  return (
    <div
      className="rounded-xl border shadow-sm overflow-hidden panel-enter"
      style={{
        backgroundColor: 'var(--card, var(--background))',
        borderColor: 'var(--border, #e5e7eb)',
        animationDelay: `${index * 60}ms`,
      }}
    >
      {/* Title bar */}
      <div
        className="flex items-center justify-between px-4 sm:px-5 py-3.5 border-b"
        style={{ borderColor: 'var(--border, #e5e7eb)' }}
      >
        <h2
          className="text-sm font-semibold"
          style={{ color: 'var(--foreground)' }}
        >
          {title}
        </h2>
        {actions && (
          <div className="flex items-center gap-2">{actions}</div>
        )}
      </div>

      {/* Content */}
      <div className="px-4 sm:px-5 py-4">{children}</div>
    </div>
  );
}

/**
 * PanelSkeleton — loading skeleton for a panel card.
 */
export function PanelSkeleton({ index = 0 }: { index?: number }) {
  return (
    <div
      className="rounded-xl border shadow-sm overflow-hidden"
      style={{
        backgroundColor: 'var(--card, var(--background))',
        borderColor: 'var(--border, #e5e7eb)',
      }}
    >
      {/* Title bar skeleton */}
      <div
        className="px-4 sm:px-5 py-3.5 border-b flex items-center"
        style={{ borderColor: 'var(--border, #e5e7eb)' }}
      >
        <div
          className="h-4 rounded animate-pulse"
          style={{
            width: `${120 + (index * 37) % 80}px`,
            backgroundColor: 'var(--muted, #e5e7eb)',
          }}
        />
      </div>
      {/* Content skeleton */}
      <div className="px-4 sm:px-5 py-4 space-y-3">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-3 rounded animate-pulse"
            style={{
              width: `${50 + (i * 23 + index * 17) % 40}%`,
              backgroundColor: 'var(--muted, #e5e7eb)',
              animationDelay: `${i * 80}ms`,
            }}
          />
        ))}
      </div>
    </div>
  );
}
